import React, {useMemo, useState} from 'react'
import PropTypes from 'prop-types'
import {FormattedMessage} from 'react-intl'
import {
    Box,
    Flex,
    Text,
    IconButton,
    Icon,
    Modal,
    ModalBody,
    ModalContent,
    ModalOverlay,
    useMultiStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {CustomClose} from '../icons'
import {noop} from '@salesforce/retail-react-app/app/utils/utils'
import {InfoCircleBlack, AlertTriangleIcon, ErrorIcon, ConfirmationIcon} from '../icons'

const DEFAULT_PRODUCT_DETAILS = '[Product name, color, size and length]'
const DEFAULT_TITLE = "We've updated your cart"

const buildLegacyCategory = ({categoryTitle, productDetails, description}) => {
    const hasProductDetails =
        productDetails !== null && productDetails !== undefined && productDetails !== ''

    const defaultDescription = "was removed because it's no longer available."
    const detailMessage = description || defaultDescription
    const message = hasProductDetails ? (
        <>
            <strong>{productDetails}</strong>
            {` ${detailMessage}`}
        </>
    ) : (
        detailMessage
    )

    return {
        key: 'legacy-item-removed',
        title: categoryTitle || 'Item removed from your cart',
        items: [message]
    }
}

const normalizeCategories = (categories, legacyConfig) => {
    if (Array.isArray(categories)) {
        return categories
            .map((category, index) => {
                const items = Array.isArray(category?.items) ? category.items.filter(Boolean) : []
                return {
                    key: category?.key || category?.title || `category-${index}`,
                    title: category?.title || 'Updates',
                    items,
                    summary: category?.summary
                }
            })
            .filter((category) => category.items.length > 0)
    }

    return [buildLegacyCategory(legacyConfig)]
}

const getSeverityIcon = (severity, props) => {
    if (severity === 'warning') {
        return <AlertTriangleIcon {...props} />
    }
    if (severity === 'error') {
        return <ErrorIcon {...props} />
    }
    if (severity === 'confirmation') {
        return <ConfirmationIcon data-testid="confirmation-icon" {...props} />
    }
    return <InfoCircleBlack {...props} />
}

const getCategorySummary = (category) => {
    if (category.summary) {
        return category.summary
    }

    const itemCount = category.items.length
    return `${itemCount} ${itemCount === 1 ? 'item was updated.' : 'items were updated.'}`
}

const CustomCallout = ({
    variant = 'full',
    severity = 'info',
    container = 'default',
    title = DEFAULT_TITLE,
    categoryTitle,
    categories,
    showMessageCount = true,
    initialTileExpanded = false,
    showMoreLabel = 'Show More',
    showLessLabel = 'Show Less',
    viewDetailsLabel = 'View details',
    hideWhenEmpty = true,
    productDetails = DEFAULT_PRODUCT_DETAILS,
    eyebrow,
    heading,
    description,
    closeAriaLabel = 'Close cart update message',
    onClose = noop,
    showCloseButton = true,
    showEyebrow = variant === 'full',
    showHeading = variant === 'full'
}) => {
    const styles = useMultiStyleConfig('CustomCallout', {})
    const [isVisible, setIsVisible] = useState(true)
    const [isTileExpanded, setIsTileExpanded] = useState(initialTileExpanded)
    const [selectedCategory, setSelectedCategory] = useState(null)

    const isCompact = variant === 'compact'
    const isTile = container === 'tile'
    const normalizedCategories = useMemo(
        () =>
            normalizeCategories(categories, {
                categoryTitle,
                productDetails,
                description
            }),
        [categories, categoryTitle, productDetails, description]
    )
    const totalMessages = normalizedCategories.reduce((acc, category) => acc + category.items.length, 0)
    const shouldRenderCallout = isVisible && (!hideWhenEmpty || totalMessages > 0)
    const shouldRenderTitleRow = showEyebrow || !isCompact
    const shouldRenderHeader = shouldRenderTitleRow || showCloseButton
    const displayTitle = showMessageCount && totalMessages > 1 ? `${title} (${totalMessages})` : title
    const shouldRenderBody = (isTile ? isTileExpanded : true) && normalizedCategories.length > 0
    const hasContentBelowHeader = shouldRenderBody || (showHeading && Boolean(heading)) || isTile

    const handleClose = () => {
        setIsVisible(false)
        onClose()
    }

    if (!shouldRenderCallout) {
        return null
    }

    return (
        <>
            <Box
                {...styles.cartItemRemovedNoticeWrapper}
                {...(isCompact ? styles.cartItemRemovedNoticeWrapperCompact : {})}
            >
                {shouldRenderHeader && (
                    <Flex
                        {...styles.cartItemRemovedNoticeHeader}
                        {...(showCloseButton
                            ? styles.cartItemRemovedNoticeHeaderWithClose
                            : styles.cartItemRemovedNoticeHeaderWithoutClose)}
                        {...(isCompact ? styles.cartItemRemovedNoticeHeaderCompact : {})}
                        {...(hasContentBelowHeader ? {} : {mb: 0})}
                    >
                        {shouldRenderTitleRow ? (
                            <Flex {...styles.cartItemRemovedNoticeTitleRow}>
                                {getSeverityIcon(severity, styles.cartItemRemovedNoticeInfoIcon)}
                                <Text {...styles.cartItemRemovedNoticeEyebrow}>
                                    {eyebrow ?? displayTitle ?? (
                                        <FormattedMessage
                                            id="cart.notification.updated_cart"
                                            defaultMessage="We've updated your cart"
                                        />
                                    )}
                                </Text>
                            </Flex>
                        ) : (
                            <Box />
                        )}

                        {showCloseButton && (
                            <IconButton
                                {...styles.cartItemRemovedNoticeCloseButton}
                                icon={<CustomClose {...styles.cartItemRemovedNoticeCloseIcon} />}
                                aria-label={closeAriaLabel}
                                onClick={handleClose}
                            />
                        )}
                    </Flex>
                )}

                {showHeading && heading && (
                    <Text {...styles.cartItemRemovedNoticeHeading}>
                        {heading}
                    </Text>
                )}

                {shouldRenderBody && (
                    <Box {...styles.bodyGeneralContainer}>
                        {normalizedCategories.map((category) => (
                            <Box key={category.key} {...styles.categoryBlock}>
                                <Text {...styles.categoryTitle}>{category.title}</Text>

                                {category.items.length >= 2 ? (
                                    <Flex {...styles.categorySummaryRow}>
                                        <Text {...styles.cartItemRemovedNoticeBody}>{getCategorySummary(category)}</Text>
                                        <Box
                                            as="button"
                                            type="button"
                                            onClick={() => setSelectedCategory(category)}
                                            {...styles.categoryActionButton}
                                        >
                                            {viewDetailsLabel}
                                        </Box>
                                    </Flex>
                                ) : (
                                    <Text {...styles.cartItemRemovedNoticeBody}>{category.items[0]}</Text>
                                )}
                            </Box>
                        ))}
                    </Box>
                )}

                {isTile && (
                    <Box
                        as="button"
                        type="button"
                        onClick={() => setIsTileExpanded((prev) => !prev)}
                        {...styles.toggleTileButton}
                    >
                        {isTileExpanded ? showLessLabel : showMoreLabel}
                    </Box>
                )}
            </Box>

            <Modal
                isOpen={Boolean(selectedCategory)}
                onClose={() => setSelectedCategory(null)}
                size="xl"
                motionPreset="none"
                isCentered={false}
            >
                <ModalOverlay />
                <ModalContent {...styles.content}>
                    <ModalBody {...styles.body}>
                        <Flex {...styles.header}>
                            <Text {...styles.title}>{selectedCategory?.title}</Text>
                            <IconButton
                                onClick={() => setSelectedCategory(null)}
                                icon={<Icon as={CustomClose} />}
                                aria-label="Close modal"
                                {...styles.closeButton}
                            />
                        </Flex>
                        <Box {...styles.detailsBody}>
                            {selectedCategory && (
                                <>
                                    {selectedCategory.items.map((item, index) => (
                                        <Text key={`${selectedCategory.key}-item-${index}`} {...styles.detailsItem}>
                                            {item}
                                        </Text>
                                    ))}
                                </>
                            )}
                        </Box>
                    </ModalBody>
                </ModalContent>
            </Modal>
        </>
    )
}

CustomCallout.propTypes = {
    categories: PropTypes.arrayOf(
        PropTypes.shape({
            key: PropTypes.string,
            summary: PropTypes.node,
            title: PropTypes.node,
            items: PropTypes.arrayOf(PropTypes.node)
        })
    ),
    categoryTitle: PropTypes.node,
    closeAriaLabel: PropTypes.string,
    container: PropTypes.oneOf(['default', 'tile']),
    description: PropTypes.node,
    eyebrow: PropTypes.node,
    heading: PropTypes.node,
    hideWhenEmpty: PropTypes.bool,
    initialTileExpanded: PropTypes.bool,
    onClose: PropTypes.func,
    productDetails: PropTypes.node,
    severity: PropTypes.oneOf(['info', 'warning', 'error', 'confirmation']),
    showCloseButton: PropTypes.bool,
    showEyebrow: PropTypes.bool,
    showHeading: PropTypes.bool,
    showLessLabel: PropTypes.node,
    showMessageCount: PropTypes.bool,
    showMoreLabel: PropTypes.node,
    title: PropTypes.node,
    variant: PropTypes.oneOf(['compact', 'full']),
    viewDetailsLabel: PropTypes.node
}

export default CustomCallout
